import {withFieldGroup} from '@cypro/ui/integrations/form'
import {Box, Text, Button} from '@chakra-ui/react'
import {LuPlus} from 'react-icons/lu'
import {QuantityBatchForm} from './quantity-batch-form'
import {Batch, BatchQuantityError, ExtrasBatchesSchema, RestVialItem} from '@cypro/engine/schema'
import {OutOfStockAlert} from '../out-of-stock-alert'
import {RestVialRow} from './rest-vial-row'

interface OrderSubstancesFormProps {
  title: string
  openAddBatchDialog: () => void
  batches: Batch[]
  addBatch: (batchId: string) => void
  onRemoveBatch?: (batchId: string) => void
  onIsolateBatch?: (batchId: string) => void
  prescribedNameIds?: string[]
  isConsumable?: boolean
  drugCatalogItemIds?: string[]
  isSolvent?: boolean
  batchErrors?: BatchQuantityError[]
  onValuesChange?: () => void
  restVials?: RestVialItem[]
  onRemoveRestVial?: (restVialId: string) => void
  notEnoughAmount: {
    name: string
    enough: boolean
  }[]
  missingTradeNames?: string[]
  /** products whose trade names are all inactive in the catalogue (CY-259) */
  inactiveTradeNames?: string[]
  // what the section must hold, shown under the title (e.g. the carrier bag size)
  helperText?: string
}

export const OrderSubstancesForm = withFieldGroup({
  defaultValues: {batches: []} as ExtrasBatchesSchema,
  props: {} as OrderSubstancesFormProps,
  render: ({
    group,
    title,
    openAddBatchDialog,
    batches,
    addBatch,
    prescribedNameIds,
    drugCatalogItemIds,
    onRemoveBatch,
    onIsolateBatch,
    notEnoughAmount,
    missingTradeNames,
    inactiveTradeNames,
    isSolvent,
    batchErrors,
    onValuesChange,
    restVials,
    onRemoveRestVial,
    helperText,
  }) => {
    return (
      <Box
        flex="1"
        display={'flex'}
        flexDirection={'column'}
        borderColor="border"
        borderWidth={'1px'}
        borderRadius="md"
        paddingRight="3"
        paddingLeft="1"
        paddingBlock="1"
        minWidth="28rem"
        maxWidth="28rem"
        bg="white"
      >
        <Box
          display={'flex'}
          gap={1}
          alignItems={'center'}
          justifyContent={'space-between'}
          paddingInline="2"
          paddingBlock="1.5"
        >
          <Text fontSize="sm" fontWeight="medium">
            {title}
          </Text>
          <Button variant={'ghost'} colorPalette="cyan" size="sm" onClick={openAddBatchDialog}>
            <LuPlus />
          </Button>
        </Box>
        {helperText && (
          <Text fontSize="xs" color="fg.muted" paddingInline="2" paddingBottom="1.5">
            {helperText}
          </Text>
        )}

        {restVials && restVials.length > 0 && (
          <Box borderTopWidth="1px" borderColor="border" marginTop="1">
            {restVials.map(rv => (
              <RestVialRow
                key={rv.restVialId}
                restVial={rv}
                batches={batches}
                onRemove={() => onRemoveRestVial?.(rv.restVialId)}
              />
            ))}
          </Box>
        )}
        <group.Field name="batches" mode="array">
          {field =>
            field?.state?.value?.map((batch, i) => {
              const key = `batches-${batch.batchId}-${i}`
              return (
                <QuantityBatchForm
                  key={key}
                  form={group}
                  fields={`batches[${i}]`}
                  batches={batches}
                  addBatch={addBatch}
                  prescribedNameIds={prescribedNameIds}
                  drugCatalogItemIds={drugCatalogItemIds}
                  onValuesChange={onValuesChange}
                  onRemove={() => {
                    field.removeValue(i)
                    if (onRemoveBatch) {
                      onRemoveBatch(batch.batchId)
                    }
                  }}
                  onIsolateBatch={() => onIsolateBatch && onIsolateBatch(batch.batchId)}
                  batchErrors={batchErrors}
                />
              )
            })
          }
        </group.Field>
        {inactiveTradeNames && inactiveTradeNames.length > 0 && (
          <Box marginTop="2">
            <OutOfStockAlert outOfStockItems={inactiveTradeNames} inactiveTradeName />
          </Box>
        )}
        {notEnoughAmount.length > 0 && (
          <Box marginTop="2">
            <OutOfStockAlert
              outOfStockItems={notEnoughAmount.map(item => item.name)}
              missingTradeNames={missingTradeNames}
              isSolvent={isSolvent}
              notEnoughAmount
            />
          </Box>
        )}
      </Box>
    )
  },
})
